import { FilterTypeValue } from "@/components/filter/filter.types.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import useEmployeeSearchInline, {
  useEmployeesByIds,
} from "@/queries/employees/use-employees.query.ts";
import { useDepartmentsSearchInline } from "@/queries/use-departments.query.ts";
import { useTeamsByIds, useTeamSearchInline } from "@/queries/use-teams.query.ts";
import { ApiGoalOwnerType, GoalOwnerTypeItems } from "@/types/goals.types.ts";
import { FilterItem, Multiselect, Select, SelectItem, Stack } from "@skailer-com/uikit";
import { useMemo } from "react";
import { Control, Controller, FieldErrors, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface GoalOwnerFilterRenderProps {
  item: FilterItem<FilterTypeValue>;
  control: Control;
  errors: FieldErrors;
}

const getSingleValue = <T,>(values: T[] | undefined): T | undefined =>
  values?.length === 1 ? values[0] : undefined;

export const GoalOwnerFilterRender = ({ item, control, errors }: GoalOwnerFilterRenderProps) => {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const ownerTypes = useWatch({ control, name: item.name }) as ApiGoalOwnerType[] | undefined;
  const ownerIds = useWatch({ control, name: "owner_id" }) as number[] | undefined;
  const ownerType = getSingleValue(ownerTypes);
  const ownerTypeOptions = item.options ?? GoalOwnerTypeItems();

  const isEmployeeOwner = ownerType === ApiGoalOwnerType.employee;
  const isDepartmentOwner = ownerType === ApiGoalOwnerType.department;
  const isTeamOwner = ownerType === ApiGoalOwnerType.team;

  const employeeSearch = useEmployeeSearchInline(isEmployeeOwner);
  const { data: selectedEmployees } = useEmployeesByIds(ownerIds ?? [], isEmployeeOwner);
  const departments = useDepartmentsSearchInline(isDepartmentOwner);
  const teams = useTeamSearchInline(isTeamOwner);
  const { data: selectedTeams, isLoading: isSelectedTeamsLoading } = useTeamsByIds(
    ownerIds ?? [],
    isTeamOwner
  );

  const employeeOptions = useMemo<SelectItem[]>(() => {
    const selectedOptions = selectedEmployees?.map((employee) => ({
      id: employee.id,
      title: renderEmployeeName(employee),
    }));

    return Array.from(
      new Map(
        [...employeeSearch.options, ...(selectedOptions ?? [])].map((option) => [option.id, option])
      ).values()
    );
  }, [employeeSearch.options, renderEmployeeName, selectedEmployees]);
  const teamOptions = useMemo<SelectItem[]>(() => {
    const options = [...teams.options, ...selectedTeams];
    return Array.from(new Map(options.map((team) => [team.id, team])).values()).map((team) => ({
      id: team.id,
      title: team.title,
    }));
  }, [selectedTeams, teams.options]);

  const renderOwnerSelect = (value: number[] | undefined, onChange: (value: number[]) => void) => {
    const selectedValues = value ?? [];

    if (isEmployeeOwner) {
      return (
        <Multiselect
          label={t("core:employee")}
          emptyTitle={t("common:select")}
          values={selectedValues}
          options={employeeOptions}
          searchOptions={employeeSearch.options}
          onSearchOptions={employeeSearch.search}
          onChange={(nextValues) => onChange(nextValues.map(Number))}
          isMulti
          searchable
          isClearable
          showAvatar
          showCaption
          loadOptionsOnOpen
          isLoading={employeeSearch.isLoading}
          error={errors.owner_id?.message as string}
        />
      );
    }

    if (isDepartmentOwner) {
      return (
        <Multiselect
          label={t("core:department")}
          emptyTitle={t("common:select")}
          values={selectedValues}
          options={departments.options}
          onSearchOptions={departments.search}
          onChange={(nextValues) => onChange(nextValues.map(Number))}
          isMulti
          searchable
          isClearable
          isLoading={departments.isLoading}
          error={errors.owner_id?.message as string}
        />
      );
    }

    if (isTeamOwner) {
      return (
        <Multiselect
          label={t("core:team")}
          emptyTitle={t("common:select")}
          values={selectedValues}
          options={teamOptions}
          searchOptions={teams.options}
          onSearchOptions={teams.search}
          onChange={(nextValues) => onChange(nextValues.map(Number))}
          isMulti
          searchable
          isClearable
          loadOptionsOnOpen
          isLoading={teams.isLoading || isSelectedTeamsLoading}
          error={errors.owner_id?.message as string}
        />
      );
    }

    return null;
  };

  return (
    <Controller
      control={control}
      name="owner_id"
      render={({ field: ownerField }) => (
        <Stack>
          <Controller
            control={control}
            name={item.name}
            render={({ field: ownerTypeField }) => (
              <Select
                label={item.label ?? t("goals:owner_type")}
                emptyTitle={t("common:select")}
                value={ownerType}
                options={ownerTypeOptions}
                onChange={(nextValue) => {
                  ownerTypeField.onChange(nextValue == null ? [] : [nextValue]);
                  ownerField.onChange([]);
                }}
                isClearable
                error={errors[item.name]?.message as string}
              />
            )}
          />
          {renderOwnerSelect(ownerField.value as number[] | undefined, ownerField.onChange)}
        </Stack>
      )}
    />
  );
};
